
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Package, ArrowRight } from 'lucide-react';
import { useMaterials } from '../hooks/useApi';
import analytics from '../utils/analytics';
import { usePageTitle } from '../hooks/usePageTitle';

const MaterialsPage: React.FC = () => {
  // 设置页面标题和描述
  usePageTitle(
    '器件材料',
    '提供专业的窑炉器件及材料，满足不同工况需求'
  );

  // 从静态 JSON 获取器件材料数据
  const { data: materials, loading, error } = useMaterials({
    limit: 50
  });

  // 加载状态
  if (loading) {
    return (
      <div className="pt-16">
        <section className="section-padding bg-gradient-primary">
          <div className="container-max text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              器件材料
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              提供专业的窑炉器件及材料，满足不同工况需求
            </p>
          </div>
        </section>
        <section className="section-padding bg-white">
          <div className="container-max">
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <span className="ml-4 text-gray-600">加载中...</span>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="pt-16">
        <section className="section-padding bg-gradient-primary">
          <div className="container-max text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              器件材料
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              提供专业的窑炉器件及材料，满足不同工况需求
            </p>
          </div>
        </section>
        <section className="section-padding bg-white">
          <div className="container-max">
            <div className="text-center py-20">
              <div className="text-red-500 mb-4">
                <Package className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                加载失败
              </h3>
              <p className="text-gray-500 mb-4">
                {error}
              </p>
              <button 
                onClick={() => window.location.reload()} 
                className="btn-primary"
              >
                重新加载
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-primary">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              器件材料
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              提供专业的窑炉器件及材料，满足不同工况需求
            </p>
          </motion.div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          {/* Materials Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {materials.map((material, index) => (
              <motion.div
                key={material.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/materials/${material.id}`}
                  className="card group hover:scale-105 transition-all duration-300 block h-full flex flex-col"
                  onClick={() => analytics.material.clickCard(material.title)}
                >
                  <div className="relative overflow-hidden rounded-t-xl bg-gray-100">
                    <img
                      src={material.image || '/images/placeholder-product.jpg'}
                      alt={material.title}
                      className="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/images/placeholder-product.jpg';
                      }}
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                      {material.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600 mb-4">
                      <Package className="w-4 h-4 mr-1" />
                      <span>{material.title_en || '专业器件'}</span>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4 flex-1">
                      {material.description}
                    </p>
                    <div className="flex items-center text-primary-600 text-sm font-medium mt-auto">
                      <span>查看详情</span>
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {materials.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-gray-400 mb-4">
                <Package className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                暂无器件
              </h3>
              <p className="text-gray-500">
                请联系我们的销售团队了解更多产品信息
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              需要更多器件信息？
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              我们提供专业的窑炉器件及材料，可根据您的具体需求提供定制化解决方案
            </p>
            <div className="flex justify-center">
              <Link
                to="/contact"
                className="btn-primary"
              >
                联系我们
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MaterialsPage;
